import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunk,
  createAxiosAsyncThunkWithParams,
  type DynamicUrl,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

export interface FaultReportState {
  plannedFaultReports: FaultReportShape[]
  unPlannedFaultReports: FaultReportShape[]
  singleFaultReport: FaultReportDetailShape | null
  faultReportOptions: FaultReportOptions
  faultReportUpdates: FaultReportUpdateShape[]
  loading: LoadingStates
  faultReportUpdateLoading: LoadingStates
  submitLoading: LoadingStates
  deleteLoading: LoadingStates
  optionsLoading: LoadingStates
}

const initialState: FaultReportState = {
  plannedFaultReports: [],
  unPlannedFaultReports: [],
  singleFaultReport: null,
  faultReportOptions: {
    type: []
  },
  faultReportUpdates: [],
  loading: 'idle',
  faultReportUpdateLoading: 'idle',
  submitLoading: 'idle',
  deleteLoading: 'idle',
  optionsLoading: 'idle'
}

export interface FaultReportOptions {
  type: []
}

interface FetchFaultReportOptions {
  field_choices: FaultReportOptions
}

const fetchFaultReportOptions = createAxiosAsyncThunk<FetchFaultReportOptions, undefined>(
  'faultReport/fetchFaultReportOptions',
  '/api/v1/sitecontent/fault-reports/',
  'OPTIONS'
)

export enum FaultReportType {
  planned = 'planned',
  unplanned = 'unplanned'
}

export interface FaultReportShape {
  id: number
  title_fi: string
  type: FaultReportType
  description_fi: string | null
  updated: Date
}

export interface FaultReportDetailShape {
  id: number | null
  type: FaultReportType
  title_fi: string
  description_fi: string | null
  start_time: Date | null
  created: Date | null
  updated: Date | null
}

type FetchFaultReportResponse = FaultReportDetailShape

const fetchFaultReport = createAxiosAsyncThunkWithParams<FetchFaultReportResponse, DynamicUrl>(
  'faultReport/fetchFaultReport',
  '/api/v1/sitecontent/fault-reports'
)

type FetchFaultReportsResponse = FaultReportShape[]

const fetchFaultReports = createAxiosAsyncThunk<FetchFaultReportsResponse, undefined>(
  'faultReport/fetchFaultReports',
  '/api/v1/sitecontent/fault-reports/'
)

type CreateFaultReportRequest = FaultReportDetailShape

type CreateFaultReportResponse = FaultReportDetailShape

const createFaultReport = createAsyncThunk<
  CreateFaultReportResponse,
  CreateFaultReportRequest,
  { rejectValue: ValidationErrors }
>('faultReport/createFaultReport', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateFaultReportResponse>(
      `/api/v1/sitecontent/fault-reports/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type ChangeFaultReportRequest = FaultReportDetailShape

type ChangeFaultReportResponse = FaultReportDetailShape

const changeFaultReport = createAsyncThunk<
  ChangeFaultReportResponse,
  ChangeFaultReportRequest,
  {
    rejectValue: ValidationErrors
  }
>('faultReport/changeFaultReport', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<ChangeFaultReportResponse>(
      `/api/v1/sitecontent/fault-reports/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteFaultReportResponse = Record<string, never>

const deleteFaultReport = createAxiosAsyncThunkWithParams<DeleteFaultReportResponse, DynamicUrl>(
  'faultReport/deleteFaultReport',
  '/api/v1/sitecontent/fault-reports',
  'DELETE'
)

/** Fault report update **/

export interface FaultReportUpdateShape {
  id: number | null
  description_fi: string
  updated: Date | null
}

interface FetchFaultReportUpdatesRequest {
  faultReportId: number
}

type FetchFaultReportUpdatesResponse = FaultReportUpdateShape[]

const fetchFaultReportUpdates = createAsyncThunk<
  FetchFaultReportUpdatesResponse,
  FetchFaultReportUpdatesRequest,
  {
    rejectValue: ValidationErrors
  }
>('faultReport/fetchFaultReportUpdates', async (data, { rejectWithValue }) => {
  try {
    const { faultReportId } = data
    const response = await getAxiosInstance().get<FetchFaultReportUpdatesResponse>(
      `/api/v1/sitecontent/fault-reports/${faultReportId}/updates/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

interface CreateFaultReportUpdateRequest extends FaultReportUpdateShape {
  faultReportId: number
}

type CreateFaultReportUpdateResponse = FaultReportUpdateShape

const createFaultReportUpdate = createAsyncThunk<
  CreateFaultReportUpdateResponse,
  CreateFaultReportUpdateRequest,
  { rejectValue: ValidationErrors }
>('faultReport/createFaultReportUpdate', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateFaultReportUpdateResponse>(
      `/api/v1/sitecontent/fault-reports/${fields.faultReportId}/updates/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

interface ChangeFaultReportUpdateRequest extends FaultReportUpdateShape {
  faultReportId: number
}

type ChangeFaultReportUpdateResponse = FaultReportUpdateShape

const changeFaultReportUpdate = createAsyncThunk<
  ChangeFaultReportUpdateResponse,
  ChangeFaultReportUpdateRequest,
  {
    rejectValue: ValidationErrors
  }
>('faultReport/changeFaultReportUpdate', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<ChangeFaultReportUpdateResponse>(
      `/api/v1/sitecontent/fault-reports/${fields.faultReportId}/updates/${fields.id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

interface DeleteFaultReportUpdateRequest {
  faultReportId: number
  id: number
}
type DeleteFaultReportUpdateResponse = Record<string, never>

const deleteFaultReportUpdate = createAsyncThunk<
  DeleteFaultReportUpdateResponse,
  DeleteFaultReportUpdateRequest,
  {
    rejectValue: ValidationErrors
  }
>('faultReport/deleteFaultReportUpdate', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().delete<DeleteFaultReportUpdateResponse>(
      `/api/v1/sitecontent/fault-reports/${fields.faultReportId}/updates/${fields.id}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

const genericActions = actionGenerator('faultReport')

const faultReportSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {
    resetFaultReportUpdates: state => {
      state.faultReportUpdates = initialState.faultReportUpdates
    }
  },
  extraReducers: builder => {
    builder

      .addCase(fetchFaultReportOptions.fulfilled, (state, { payload }) => {
        state.faultReportOptions = payload.field_choices
        state.optionsLoading = 'fulfilled'
      })
      .addCase(fetchFaultReportOptions.pending, state => {
        state.faultReportOptions = initialState.faultReportOptions
        state.optionsLoading = 'pending'
      })
      .addCase(fetchFaultReportOptions.rejected, state => {
        state.optionsLoading = 'rejected'
      })

      .addCase(fetchFaultReport.fulfilled, (state, { payload }) => {
        state.singleFaultReport = payload
        state.loading = 'fulfilled'
      })
      .addCase(fetchFaultReport.pending, state => {
        state.singleFaultReport = initialState.singleFaultReport
        state.loading = 'pending'
      })
      .addCase(fetchFaultReport.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchFaultReports.fulfilled, (state, { payload }) => {
        const splitFaultReports = payload.reduce(
          (faultReportArray: [FaultReportShape[], FaultReportShape[]], faultReport) => {
            faultReportArray[faultReport.type === FaultReportType.planned ? 0 : 1].push(faultReport)
            return faultReportArray
          },
          [[], []]
        )

        state.plannedFaultReports = splitFaultReports[0]
        state.unPlannedFaultReports = splitFaultReports[1]
        state.loading = 'fulfilled'
      })
      .addCase(fetchFaultReports.pending, state => {
        state.plannedFaultReports = initialState.plannedFaultReports
        state.unPlannedFaultReports = initialState.unPlannedFaultReports
        state.loading = 'pending'
      })
      .addCase(fetchFaultReports.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(createFaultReport.fulfilled, state => {
        state.submitLoading = 'fulfilled'
      })
      .addCase(createFaultReport.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(createFaultReport.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(changeFaultReport.fulfilled, state => {
        state.submitLoading = 'fulfilled'
      })
      .addCase(changeFaultReport.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(changeFaultReport.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteFaultReport.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteFaultReport.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteFaultReport.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      /** Fault report update **/

      .addCase(fetchFaultReportUpdates.fulfilled, (state, { payload }) => {
        state.faultReportUpdates = payload
        state.faultReportUpdateLoading = 'fulfilled'
      })
      .addCase(fetchFaultReportUpdates.pending, state => {
        state.faultReportUpdates = initialState.faultReportUpdates
        state.faultReportUpdateLoading = 'pending'
      })
      .addCase(fetchFaultReportUpdates.rejected, state => {
        state.faultReportUpdateLoading = 'rejected'
      })

      .addCase(createFaultReportUpdate.fulfilled, state => {
        state.submitLoading = 'fulfilled'
      })
      .addCase(createFaultReportUpdate.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(createFaultReportUpdate.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(changeFaultReportUpdate.fulfilled, state => {
        state.submitLoading = 'fulfilled'
      })
      .addCase(changeFaultReportUpdate.pending, state => {
        state.submitLoading = 'pending'
      })
      .addCase(changeFaultReportUpdate.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteFaultReportUpdate.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteFaultReportUpdate.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteFaultReportUpdate.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default faultReportSlice.reducer
export const { resetFaultReportUpdates } = faultReportSlice.actions

export {
  changeFaultReport,
  changeFaultReportUpdate,
  createFaultReport,
  createFaultReportUpdate,
  deleteFaultReport,
  deleteFaultReportUpdate,
  fetchFaultReport,
  fetchFaultReportOptions,
  fetchFaultReports,
  fetchFaultReportUpdates
}
